import { ethers } from 'ethers';

const ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;
const READ_ONLY_RPC_URL = 'https://data-seed-prebsc-1-s1.binance.org:8545/';
const PAYMENT_TOKEN_ABI = ['function paymentToken() view returns (address)'];
const ERC20_METADATA_ABI = [
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
];
const DEFAULT_MINT_METHOD_NAMES = [
  'buy',
  'buyNft',
  'buyNFT',
  'buyTicket',
  'purchase',
  'buyToken',
  'buyTokens',
  'publicMint',
  'mint',
  'mintTicket',
  'mintNft',
  'mintNFT',
  'safeMint',
  'claim',
];
const SALE_ABI_SUFFIXES = [
  'Sale_abi',
  'sale_abi',
  'NFTSale_abi',
  'nft_sale_abi',
  'NFT_sale_abi',
  'Presale_abi',
  'presale_abi',
  'Mint_abi',
  'mint_abi',
  'PublicSale_abi',
  'public_sale_abi',
  'PublicMint_abi',
  'public_mint_abi',
  'abi',
];
const COLLECTION_ABI_SUFFIXES = [
  'NFT_abi',
  'nft_abi',
  'Collection_abi',
  'collection_abi',
  'abi',
];

const getValueByPath = (source, path) => {
  return path.split('.').reduce((value, key) => value?.[key], source);
};

const normalizeString = (value) => {
  return `${value ?? ''}`.trim();
};

const isMeaningful = (value) => {
  return normalizeString(value) !== '';
};

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toUnitString = (value, maxFractionDigits = 18) => {
  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    return numericValue.toLocaleString('fullwide', {
      useGrouping: false,
      maximumFractionDigits: Math.min(Math.max(maxFractionDigits, 0), 20),
    });
  }

  return normalizeString(value || 0);
};

const uniqueValues = (values) => {
  return [...new Set(values.filter(Boolean))];
};

const getFirstValue = (source, paths, predicate = isMeaningful) => {
  for (const path of paths) {
    const value = getValueByPath(source, path);
    if (predicate(value)) {
      return value;
    }
  }

  return null;
};

const getFirstAddress = (source, paths) => {
  const address = getFirstValue(source, paths, (candidate) => {
    return ADDRESS_REGEX.test(normalizeString(candidate));
  });

  return address ? normalizeString(address) : null;
};

const getFirstNumber = (source, paths) => {
  return getFirstValue(source, paths, (candidate) => {
    return Number.isFinite(Number(candidate));
  });
};

const splitWords = (value) => {
  return normalizeString(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
};

const buildNameVariants = (value) => {
  const normalized = normalizeString(value);

  if (!normalized) {
    return [];
  }

  const words = splitWords(normalized);
  const compact = words.join('');
  const pascalCase = words
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join('');
  const camelCase = pascalCase
    ? `${pascalCase.charAt(0).toLowerCase()}${pascalCase.slice(1)}`
    : '';

  return uniqueValues([
    normalized,
    compact,
    pascalCase,
    camelCase,
    normalized.toUpperCase(),
    normalized.toLowerCase(),
    compact.toUpperCase(),
    compact.toLowerCase(),
    words.map((word) => word.toUpperCase()).join('_'),
    words.map((word) => word.toLowerCase()).join('_'),
  ]);
};

const buildAbiKeyCandidates = (baseVariants, suffixes) => {
  const keys = [];

  for (const baseVariant of baseVariants) {
    for (const suffix of suffixes) {
      keys.push(`${baseVariant}_${suffix}`);
      keys.push(`${baseVariant}${suffix}`);
    }
  }

  return uniqueValues(keys);
};

const collectProjectValues = (project, paths) => {
  return uniqueValues(
    paths
      .map((path) => getValueByPath(project, path))
      .map((value) => normalizeString(value))
      .filter(Boolean),
  );
};

const getAbiBaseVariants = (project, paths) => {
  return uniqueValues(
    collectProjectValues(project, paths).flatMap((value) => buildNameVariants(value)),
  );
};

const getAbiByKey = (abiHistory, key) => {
  const value = abiHistory?.[key];
  const abiData = Array.isArray(value)
    ? value
    : Array.isArray(value?.data)
      ? value.data
      : null;

  if (!abiData) {
    return null;
  }

  return augmentAbiFromSource(abiData, value?.code);
};

const normalizeTypeToken = (value) => {
  return normalizeString(value)
    .replace(/\b(memory|calldata|storage|indexed)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const parseSolidityParams = (paramsString) => {
  const normalizedParams = normalizeString(paramsString);

  if (!normalizedParams) {
    return [];
  }

  return normalizedParams
    .split(',')
    .map((param) => normalizeTypeToken(param))
    .filter(Boolean)
    .map((param, index) => {
      const parts = param.split(' ').filter(Boolean);
      const hasNamedParam = parts.length > 1;
      const name = hasNamedParam ? parts[parts.length - 1] : `arg${index}`;
      const type = hasNamedParam ? parts.slice(0, -1).join(' ') : parts[0];

      return {
        internalType: type,
        name,
        type,
      };
    });
};

const parseStateMutability = (signatureTail) => {
  const normalizedTail = normalizeString(signatureTail);

  if (/\bpayable\b/.test(normalizedTail)) {
    return 'payable';
  }

  if (/\bview\b/.test(normalizedTail)) {
    return 'view';
  }

  if (/\bpure\b/.test(normalizedTail)) {
    return 'pure';
  }

  return 'nonpayable';
};

const parseFunctionsFromSoliditySource = (sourceCode) => {
  const source = normalizeString(sourceCode);

  if (!source) {
    return [];
  }

  const functions = [];
  const functionRegex = /function\s+([A-Za-z0-9_]+)\s*\(([\s\S]*?)\)\s*([^{;]*)[{;]/g;
  let match = functionRegex.exec(source);

  while (match) {
    const [, name, paramsString, signatureTail] = match;

    functions.push({
      type: 'function',
      name,
      inputs: parseSolidityParams(paramsString),
      outputs: [],
      stateMutability: parseStateMutability(signatureTail),
    });

    match = functionRegex.exec(source);
  }

  return functions;
};

const augmentAbiFromSource = (abiData, sourceCode) => {
  const parsedFunctions = parseFunctionsFromSoliditySource(sourceCode);

  if (!parsedFunctions.length) {
    return abiData;
  }

  const existingFunctionNames = new Set(
    (abiData || [])
      .filter((entry) => entry?.type === 'function')
      .map((entry) => normalizeString(entry?.name)),
  );

  const missingFunctions = parsedFunctions.filter((entry) => {
    return !existingFunctionNames.has(normalizeString(entry?.name));
  });

  if (!missingFunctions.length) {
    return abiData;
  }

  return [...abiData, ...missingFunctions];
};

const getAbiEntries = (abiHistory) => {
  return Object.entries(abiHistory || {})
    .map(([key, value]) => {
      const abi = getAbiByKey(abiHistory, key);
      return abi ? { key, abi } : null;
    })
    .filter(Boolean);
};

const getMethodNames = (abi) => {
  return uniqueValues(
    (abi || [])
      .filter((entry) => entry?.type === 'function')
      .map((entry) => normalizeString(entry?.name))
      .filter(Boolean),
  );
};

const isErc20LikeAbi = (methodNames) => {
  const signature = ['allowance', 'approve', 'transfer', 'transferFrom', 'decimals'];
  return signature.every((methodName) => methodNames.includes(methodName));
};

const scoreSaleAbiCandidate = (key, abi) => {
  const lowerKey = normalizeString(key).toLowerCase();
  const methodNames = getMethodNames(abi);
  let score = 0;

  if (lowerKey.includes('sale')) {
    score += 10;
  }

  if (lowerKey.includes('ticket')) {
    score += 2;
  }

  if (
    ['buy', 'buyNft', 'buyNFT', 'buyTicket', 'purchase'].some((name) => {
      return methodNames.includes(name);
    })
  ) {
    score += 12;
  }

  if (methodNames.includes('mintTicket')) {
    score += 6;
  }

  if (
    ['pricePerTicket', 'saleActive', 'purchasedCount', 'paymentToken', 'setSaleActive'].some(
      (name) => methodNames.includes(name),
    )
  ) {
    score += 8;
  }

  if (
    ['tokenURI', 'ownerOf', 'supportsInterface', 'safeTransferFrom'].some((name) => {
      return methodNames.includes(name);
    })
  ) {
    score -= 4;
  }

  if (isErc20LikeAbi(methodNames) || lowerKey.includes('token_abi')) {
    score -= 20;
  }

  return score;
};

const scoreCollectionAbiCandidate = (key, abi) => {
  const lowerKey = normalizeString(key).toLowerCase();
  const methodNames = getMethodNames(abi);
  let score = 0;

  if (lowerKey.includes('nft') || lowerKey.includes('collection')) {
    score += 10;
  }

  if (lowerKey.includes('ticket')) {
    score += 2;
  }

  if (
    ['tokenURI', 'ownerOf', 'supportsInterface', 'safeTransferFrom', 'balanceOf'].some(
      (name) => {
        return methodNames.includes(name);
      },
    )
  ) {
    score += 12;
  }

  if (
    ['maxSupply', 'totalMinted', 'saleContract', 'remainingSupply'].some((name) => {
      return methodNames.includes(name);
    })
  ) {
    score += 8;
  }

  if (methodNames.includes('mintTicket')) {
    score += 4;
  }

  if (isErc20LikeAbi(methodNames) || lowerKey.includes('token_abi')) {
    score -= 20;
  }

  return score;
};

const findAbiByHeuristic = (abiHistory, type) => {
  const scoredEntries = getAbiEntries(abiHistory)
    .map(({ key, abi }) => {
      const score = type === 'sale'
        ? scoreSaleAbiCandidate(key, abi)
        : scoreCollectionAbiCandidate(key, abi);

      return { key, abi, score };
    })
    .sort((a, b) => b.score - a.score);

  const bestMatch = scoredEntries[0];

  if (!bestMatch || bestMatch.score <= 0) {
    return {
      key: null,
      abi: null,
    };
  }

  return {
    key: bestMatch.key,
    abi: bestMatch.abi,
  };
};

const findAbi = (abiHistory, keys) => {
  for (const key of uniqueValues(keys)) {
    const abi = getAbiByKey(abiHistory, key);
    if (abi) {
      return {
        key,
        abi,
      };
    }
  }

  return {
    key: null,
    abi: null,
  };
};

const normalizeMethodName = (value) => {
  return normalizeString(value).replace(/\(\)$/, '');
};

const findMethodByName = (abi, methodName) => {
  const normalizedMethodName = normalizeMethodName(methodName).toLowerCase();

  if (!normalizedMethodName) {
    return null;
  }

  return (
    abi.find((entry) => {
      return (
        entry?.type === 'function' &&
        normalizeString(entry?.name).toLowerCase() === normalizedMethodName
      );
    }) || null
  );
};

const getExplicitMintMethodName = (project) => {
  return normalizeMethodName(
    getFirstValue(project, [
      'nft.sale_contract.mint_method',
      'nft.sale_contract.method_name',
      'nft.mint_method',
      'nft.method_name',
      'nft.public_mint_method',
    ]),
  );
};

const hasErc20SaleSignals = (contractAbi) => {
  const methodNames = getMethodNames(contractAbi);

  return ['paymentToken', 'pricePerTicket', 'maxPerWallet', 'purchasedCount'].some((name) => {
    return methodNames.includes(name);
  });
};

const getPaymentType = (project, mintMethod, paymentTokenAddress, contractAbi) => {
  const explicitPaymentType = normalizeString(
    getFirstValue(project, [
      'nft.sale_contract.payment_type',
      'nft.payment_type',
      'nft.sale_contract.currency',
      'nft.currency',
      'nft.payment_asset.type',
      'nft.sale_contract.payment_asset.type',
    ]),
  ).toLowerCase();

  if (['native', 'bnb', 'eth'].includes(explicitPaymentType)) {
    return 'native';
  }

  if (['erc20', 'token', 'merx', 'usdt', 'busd'].includes(explicitPaymentType)) {
    return 'erc20';
  }

  if (mintMethod?.stateMutability === 'payable') {
    return 'native';
  }

  if (paymentTokenAddress) {
    return 'erc20';
  }

  if (hasErc20SaleSignals(contractAbi)) {
    return 'erc20';
  }

  if (toFiniteNumber(project?.nft?.price_in_mercurx) > 0) {
    return 'erc20';
  }

  return 'native';
};

const getPaymentTokenAddress = (project) => {
  return (
    getFirstAddress(project, [
      'nft.sale_contract.payment_token.contract_address',
      'nft.sale_contract.payment_token.address',
      'nft.sale_contract.payment_token_address',
      'nft.payment_token.contract_address',
      'nft.payment_token.address',
      'nft.payment_token_address',
      'nft.payment_asset.contract_address',
      'nft.payment_asset.address',
    ]) || null
  );
};

const getPaymentTokenSymbol = (project, mercurxProject, paymentType) => {
  const symbol = normalizeString(
    getFirstValue(project, [
      'nft.sale_contract.payment_token.symbol',
      'nft.payment_token.symbol',
      'nft.payment_asset.symbol',
      'nft.sale_contract.currency',
      'nft.currency',
    ]),
  );

  if (symbol) {
    return symbol;
  }

  if (paymentType === 'native') {
    return 'BNB';
  }

  return mercurxProject?.token?.symbol || 'TOKEN';
};

const getPaymentTokenDecimals = (project, mercurxProject) => {
  const decimals = getFirstNumber(project, [
    'nft.sale_contract.payment_token.decimals',
    'nft.payment_token.decimals',
    'nft.payment_asset.decimals',
  ]);

  if (decimals !== null) {
    return Number(decimals);
  }

  return Number(mercurxProject?.token?.decimals) || 18;
};

const getUnitPriceInToken = (project) => {
  return toFiniteNumber(
    getFirstValue(project, [
      'nft.sale_contract.price_in_payment_token',
      'nft.sale_contract.price',
      'nft.payment_token.price',
      'nft.payment_asset.price',
      'nft.price_in_mercurx',
    ]),
  );
};

const getUnitPriceInNativeCurrency = (project, bnbData) => {
  const explicitNativePrice = toFiniteNumber(
    getFirstValue(project, [
      'nft.sale_contract.price_in_bnb',
      'nft.price_in_bnb',
      'nft.sale_contract.price_in_native',
      'nft.price_in_native',
    ]),
  );

  if (explicitNativePrice > 0) {
    return explicitNativePrice;
  }

  const bnbUsdPrice = toFiniteNumber(bnbData?.price);
  const usdPrice = toFiniteNumber(project?.nft?.price_in_usd);

  if (bnbUsdPrice > 0 && usdPrice > 0) {
    return usdPrice / bnbUsdPrice;
  }

  return 0;
};

export const isAddress = (value) => {
  return ADDRESS_REGEX.test(normalizeString(value));
};

export const getNftCollectionContractAddress = (project) => {
  const genericNftAddress = getFirstAddress(project, [
    'nft.address',
    'nft.nft_address',
    'nft.contract_address',
    'nft.contract.address',
  ]);
  const nestedContractAddress = getFirstAddress(project, [
    'nft.collection.contract_address',
    'nft.collection.address',
    'nft.nft_contract.contract_address',
    'nft.nft_contract.address',
  ]);

  return genericNftAddress || nestedContractAddress || normalizeString(
    getFirstValue(project, ['nft.nft_contract_id']),
  );
};

export const getNftSaleContractAddress = (project) => {
  const explicitSaleContractAddress = getFirstAddress(project, [
    'nft.sale_contract.contract_address',
    'nft.presale_contract.contract_address',
    'nft.sale_contract_address',
    'nft.mint_contract.contract_address',
    'nft.mint_contract.address',
  ]);

  if (explicitSaleContractAddress) {
    return explicitSaleContractAddress;
  }

  const genericNftAddress = getFirstAddress(project, [
    'nft.address',
    'nft.nft_address',
    'nft.contract_address',
    'nft.contract.address',
  ]);
  const nestedContractAddress = getFirstAddress(project, [
    'nft.collection.contract_address',
    'nft.collection.address',
    'nft.nft_contract.contract_address',
    'nft.nft_contract.address',
  ]);

  if (
    genericNftAddress &&
    nestedContractAddress &&
    genericNftAddress.toLowerCase() !== nestedContractAddress.toLowerCase()
  ) {
    return nestedContractAddress;
  }

  return nestedContractAddress || genericNftAddress || null;
};

// Resolves the ERC20 used to pay for mint: backend data first, then the sale
// contract's paymentToken(). Symbol/decimals are read from the token contract.
export const fetchNftPaymentToken = async (project) => {
  const provider = new ethers.providers.JsonRpcProvider(READ_ONLY_RPC_URL);
  let address = getPaymentTokenAddress(project);

  if (!address) {
    const saleContractAddress = getNftSaleContractAddress(project);
    if (!isAddress(saleContractAddress)) {
      return null;
    }
    try {
      const saleContract = new ethers.Contract(saleContractAddress, PAYMENT_TOKEN_ABI, provider);
      address = await saleContract.paymentToken();
    } catch (error) {
      return null;
    }
  }

  if (!isAddress(address) || address === ethers.constants.AddressZero) {
    return null;
  }

  const tokenContract = new ethers.Contract(address, ERC20_METADATA_ABI, provider);
  const [symbol, decimals] = await Promise.all([
    tokenContract.symbol().catch(() => null),
    tokenContract.decimals().catch(() => null),
  ]);

  if (!symbol || decimals === null) {
    return null;
  }

  return { address, symbol, decimals: Number(decimals) };
};

export const getNftDisplayContractAddress = (project) => {
  return (
    getNftSaleContractAddress(project) ||
    getNftCollectionContractAddress(project) ||
    normalizeString(getFirstValue(project, ['nft.nft_contract_id']))
  );
};

export const resolveNftCollectionConfig = (project, abiHistory) => {
  const collectionAbiBaseVariants = getAbiBaseVariants(project, [
    'nft.contract_name',
    'nft.nft_contract.contract_name',
    'nft.nft_contract.name',
    'nft.collection.contract_name',
    'nft.collection.name',
    'nft.symbol',
    'nft.name',
    'project_name',
    'name',
  ]);
  const explicitCollectionAbiKey = normalizeString(
    getFirstValue(project, [
      'nft.abi_key',
      'nft.contract_abi_key',
      'nft.collection.abi_key',
      'nft.nft_contract.abi_key',
    ]),
  );
  const collectionAbiLookup = findAbi(abiHistory, [
    explicitCollectionAbiKey,
    ...buildAbiKeyCandidates(collectionAbiBaseVariants, COLLECTION_ABI_SUFFIXES),
  ]);
  const fallbackCollectionAbiLookup = collectionAbiLookup.abi
    ? collectionAbiLookup
    : findAbiByHeuristic(abiHistory, 'collection');

  return {
    collectionContractAddress: getNftCollectionContractAddress(project),
    displayContractAddress: getNftDisplayContractAddress(project),
    contractAbi: fallbackCollectionAbiLookup.abi,
    contractAbiKey: fallbackCollectionAbiLookup.key,
  };
};

export const resolveNftSaleConfig = (project, abiHistory, mercurxProject, bnbData) => {
  const saleAbiBaseVariants = getAbiBaseVariants(project, [
    'nft.sale_contract.contract_name',
    'nft.sale_contract.name',
    'nft.sale_contract.title',
    'nft.sale_contract.label',
    'nft.mint_contract.contract_name',
    'nft.mint_contract.name',
    'nft.mint_contract.title',
    'nft.symbol',
    'nft.name',
    'project_name',
    'name',
  ]);
  const collectionAbiBaseVariants = getAbiBaseVariants(project, [
    'nft.contract_name',
    'nft.nft_contract.contract_name',
    'nft.nft_contract.name',
    'nft.collection.contract_name',
    'nft.collection.name',
    'nft.symbol',
    'nft.name',
    'project_name',
    'name',
  ]);
  const explicitSaleAbiKey = normalizeString(
    getFirstValue(project, [
      'nft.sale_contract.abi_key',
      'nft.sale_contract.contract_abi_key',
      'nft.sale_abi_key',
      'nft.presale_abi_key',
    ]),
  );
  const explicitCollectionAbiKey = normalizeString(
    getFirstValue(project, [
      'nft.abi_key',
      'nft.contract_abi_key',
      'nft.collection.abi_key',
      'nft.nft_contract.abi_key',
    ]),
  );

  const saleAbiLookup = findAbi(abiHistory, [
    explicitSaleAbiKey,
    ...buildAbiKeyCandidates(saleAbiBaseVariants, SALE_ABI_SUFFIXES),
  ]);
  const collectionAbiLookup = findAbi(abiHistory, [
    explicitCollectionAbiKey,
    ...buildAbiKeyCandidates(collectionAbiBaseVariants, COLLECTION_ABI_SUFFIXES),
  ]);
  const fallbackSaleAbiLookup = saleAbiLookup.abi
    ? saleAbiLookup
    : findAbiByHeuristic(abiHistory, 'sale');
  const fallbackCollectionAbiLookup = collectionAbiLookup.abi
    ? collectionAbiLookup
    : findAbiByHeuristic(abiHistory, 'collection');
  const contractAbi = fallbackSaleAbiLookup.abi || fallbackCollectionAbiLookup.abi;
  const explicitMintMethodName = getExplicitMintMethodName(project);
  const mintMethodNameCandidates = uniqueValues([
    explicitMintMethodName,
    ...DEFAULT_MINT_METHOD_NAMES,
  ]);
  const mintMethod = contractAbi
    ? mintMethodNameCandidates
      .map((methodName) => findMethodByName(contractAbi, methodName))
      .find(Boolean) || null
    : null;
  const paymentTokenAddress = getPaymentTokenAddress(project);
  const paymentType = getPaymentType(project, mintMethod, paymentTokenAddress, contractAbi);

  return {
    saleContractAddress: getNftSaleContractAddress(project),
    collectionContractAddress: getNftCollectionContractAddress(project),
    displayContractAddress: getNftDisplayContractAddress(project),
    contractAbi,
    contractAbiKey: fallbackSaleAbiLookup.key || fallbackCollectionAbiLookup.key,
    mintMethod,
    mintMethodName: mintMethod?.name || explicitMintMethodName || null,
    paymentType,
    paymentTokenAddress,
    paymentTokenSymbol: getPaymentTokenSymbol(project, mercurxProject, paymentType),
    paymentTokenDecimals: getPaymentTokenDecimals(project, mercurxProject),
    unitPriceInToken: getUnitPriceInToken(project),
    unitPriceInNativeCurrency: getUnitPriceInNativeCurrency(project, bnbData),
  };
};

export const buildMintFunctionArgs = (mintMethod, { quantity, signerAddress }) => {
  const inputs = mintMethod?.inputs || [];
  const normalizedQuantity = String(quantity);

  if (inputs.length === 0) {
    return [];
  }

  if (inputs.length === 1) {
    if (inputs[0]?.type === 'address') {
      return [signerAddress];
    }

    if (inputs[0]?.type?.startsWith('uint')) {
      return [normalizedQuantity];
    }
  }

  if (inputs.length === 2) {
    if (inputs[0]?.type === 'address' && inputs[1]?.type?.startsWith('uint')) {
      return [signerAddress, normalizedQuantity];
    }

    if (inputs[0]?.type?.startsWith('uint') && inputs[1]?.type === 'address') {
      return [normalizedQuantity, signerAddress];
    }
  }

  throw new Error(
    `${mintMethod?.name || 'Mint'} function signature is not supported ` +
      'by the generic NFT mint flow.',
  );
};

export const toBaseUnits = (value, decimals) => {
  return ethers.utils
    .parseUnits(toUnitString(value, Math.max(decimals, 18)), decimals)
    .toString();
};
